/**
 * The desk a score is laid on: a dark ground, white sheets, and a zoom over them.
 *
 * Every notation program puts the page on something darker than the page, and for a reason
 * that is not decoration: paper with no edge has no size, and a score that fills its frame
 * edge to edge reads as a picture of music rather than as a page of it. Each sheet casts a
 * shadow on the ground for the same reason. The caller lays one engraved page on it, styled
 * {@link SHEET}, and says which by `sheet`: the desk holds a page at a time, and a new one
 * is shown from its top.
 *
 * While the music plays, the desk scrolls to the system it is in: see `use-playhead.ts`.
 *
 * Zoom is a transform and not a re-engraving. Asking the engine for another scale would
 * re-break the systems, so the bar you were looking at would move and the line you were
 * reading would end somewhere else -- which is not what zooming means. The engraving is
 * fixed and the picture of it is scaled, so the page is the same page, larger; that is
 * `page view` in every editor that has one, and the SVG stays sharp at any size.
 *
 * Two fits are offered. `page` is the whole sheet in the frame, top to bottom, which is what
 * a score is watched at while it plays: nothing to scroll, and the page turns when the music
 * runs off it. `width` is the sheet across the frame, which is larger on a tall page and is
 * read by scrolling. Both are resolved here rather than by the caller because only this
 * component knows both halves of the sum -- how big the frame is and how big the engraving
 * came out -- and both change without the caller hearing about it: the window is resized, a
 * desk goes full screen, or a re-render lands.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useFollowPlayhead } from "../lib/use-playhead";
import { useTopOnTurn } from "../lib/use-shown-page";

/** A magnification, or the instruction to work one out from the frame: all of the page, or its width. */
export type Zoom = number | "page" | "width";

export interface ScoreCanvasProps {
  readonly zoom: Zoom;
  /** The magnification actually in force, for a control that has to print it. */
  onResolved?(zoom: number): void;
  /** Keep the playhead line in view, which is while the music plays. */
  readonly following?: boolean;
  /** Which page is on the desk; a change of it shows the new page from the top. */
  readonly sheet: number;
  /** The page, with the {@link SHEET} class. */
  readonly children: ReactNode;
}

/** How a page looks on the desk: white, with a shadow on the ground. */
export const SHEET = "bg-white shadow-sheet";

/** The ground showing around the sheet, in pixels. Also the room a fit leaves itself. */
const MARGIN = 20;

/** How far a fitted page may be blown up. Past this a short score is all stave and no music. */
const FIT_CEILING = 2;

/** Measured size of an element, kept in state so a change of it redraws. */
function useMeasured(): [React.RefObject<HTMLDivElement>, { width: number; height: number }] {
  const ref = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  useLayoutEffect(() => {
    const node = ref.current;
    if (node === null) return;
    // offsetWidth is the laid-out size and ignores any transform above it, which is exactly
    // what a zoom has to be worked out from: the size the page would be at 1.
    const read = (): void =>
      setSize((was) =>
        was.width === node.offsetWidth && was.height === node.offsetHeight
          ? was
          : { width: node.offsetWidth, height: node.offsetHeight },
      );
    read();
    const watching = new ResizeObserver(read);
    watching.observe(node);
    return () => watching.disconnect();
  }, []);

  return [ref, size];
}

export default function ScoreCanvas({
  zoom,
  onResolved,
  following = false,
  sheet: shown,
  children,
}: ScoreCanvasProps): JSX.Element {
  const [frame, frameSize] = useMeasured();
  const [sheet, sheetSize] = useMeasured();
  useTopOnTurn(frame, shown);
  useFollowPlayhead(frame, following);

  const across =
    frameSize.width === 0 || sheetSize.width === 0
      ? 1
      : Math.min(FIT_CEILING, (frameSize.width - MARGIN * 2) / sheetSize.width);
  const down =
    frameSize.height === 0 || sheetSize.height === 0
      ? across
      : (frameSize.height - MARGIN * 2) / sheetSize.height;
  const scale = zoom === "width" ? across : zoom === "page" ? Math.min(across, down) : zoom;

  const told = useRef<number | null>(null);
  useEffect(() => {
    if (onResolved === undefined || told.current === scale) return;
    told.current = scale;
    onResolved(scale);
  }, [onResolved, scale]);

  return (
    <div ref={frame} className="min-h-0 flex-1 overflow-auto bg-canvas" style={{ padding: MARGIN }}>
      {/* The scaled picture takes no room in the layout, so this spacer is given the size the
          picture appears to be. Without it the ground never learns it has to scroll. */}
      <div
        className="mx-auto"
        style={{ width: sheetSize.width * scale, height: sheetSize.height * scale }}
      >
        <div
          ref={sheet}
          className="w-fit"
          style={{ transform: `scale(${scale})`, transformOrigin: "top left" }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

/** The steps the zoom control offers, as multiples of the engraved size. */
export const ZOOMS = [0.5, 0.75, 1, 1.25, 1.5, 2, 3] as const;

/** Move one step up or down the ladder from wherever the zoom is now. */
export function stepped(from: number, by: 1 | -1): number {
  const ladder = [...ZOOMS];
  if (by === 1) return ladder.find((one) => one > from + 0.001) ?? (ladder.at(-1) as number);
  return [...ladder].reverse().find((one) => one < from - 0.001) ?? (ladder[0] as number);
}

/** A zoom control: out, in, and the ladder itself with the two fits on top. */
export function ZoomControl({
  zoom,
  resolved,
  onZoom,
  className,
}: {
  readonly zoom: Zoom;
  /** What a fit came out as, so the menu can print the number it chose. */
  readonly resolved: number;
  onZoom(zoom: Zoom): void;
  readonly className?: string;
}): JSX.Element {
  const step = useCallback((by: 1 | -1) => onZoom(stepped(resolved, by)), [onZoom, resolved]);
  const button =
    "h-7 w-7 rounded text-[15px] leading-none text-shell-text hover:bg-shell-hot disabled:opacity-40";

  return (
    <div className={`flex items-center gap-1 ${className ?? ""}`}>
      <button
        type="button"
        onClick={() => step(-1)}
        disabled={resolved <= ZOOMS[0]}
        aria-label="Zoom out"
        className={button}
      >
        −
      </button>
      <select
        value={String(zoom)}
        onChange={(event) => {
          const picked = event.target.value;
          onZoom(picked === "page" || picked === "width" ? picked : Number(picked));
        }}
        aria-label="Zoom"
        className="h-7 rounded bg-shell-key px-1.5 font-mono text-[11px] text-shell-lit outline-none hover:bg-shell-hot"
      >
        <option value="page">
          Fit page{zoom === "page" ? ` (${Math.round(resolved * 100)}%)` : ""}
        </option>
        <option value="width">
          Fit width{zoom === "width" ? ` (${Math.round(resolved * 100)}%)` : ""}
        </option>
        {ZOOMS.map((one) => (
          <option key={one} value={one}>
            {one * 100}%
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={() => step(1)}
        disabled={resolved >= (ZOOMS.at(-1) as number)}
        aria-label="Zoom in"
        className={button}
      >
        +
      </button>
    </div>
  );
}
